import { ConnectedRouter } from 'connected-next-router';
import { memo } from 'react';

import { type GetByShortIDCollectionResponse, type ScopedPermissions } from '@air/api';
import { type ShortUrlResponse } from '@air/api/types';
import { ActionBarProvider } from '@air/provider-action-bar';
import { ModalProvider } from '@air/provider-modal';
import { ShortIdProvider } from '@air/provider-short-id';
import { ErrorBoundary } from '@air/utils-error';

import { AccountProvisionerProvider } from '~/components/AccountProvisioner/providers/AccountProvisionerProvider';
import { MaintenanceMode } from '~/components/MaintenanceMode/MaintenanceMode';
import { PublicBoardSocketContextProvider } from '~/components/PublicBoardSocketContextProvider';
import { PublicPermissionsProvider } from '~/components/PublicPermissionsProvider';
import { PublicShareWorkspaceAccessProvider } from '~/components/PublicShareWorkspaceAccessProvider';
import { PublicWorkspaceProvider } from '~/components/PublicWorkspaceProvider';
import { KanbanOffsetContextProvider } from '~/components/v2/components/shared/kanban/KanbanOffsetContextProvider';
import { TableHeaderPositionContextProvider } from '~/components/v2/components/shared/table/TableHeaderPositionContextProvider';
import {
  PublicShowcasePage,
  type PublicShowcasePageProps,
} from '~/components/v2/components/showcase/PublicShowcasePage';
import { PublicShowcaseRequireAccountGate } from '~/components/v2/components/showcase/PublicShowcaseRequireAccountGate';
import { ThumbnailPreferenceContextProvider } from '~/hooks/useThumbnailPreference';
import { PublicAccountProvider } from '~/providers/AccountProvider';
import { ExternalLinksClickableProvider } from '~/providers/ExternalLinksClickableProvider';
import { FiltersProvider } from '~/providers/FiltersProvider';
import { ModifiersKeysPressedProvider } from '~/providers/ModifiersKeysPressedProvider';
import { PublicBoardCustomFieldsProvider } from '~/providers/PublicBoardCustomFieldsProvider';
import { type PublicShowcaseContextValue, PublicShowcaseProvider } from '~/providers/PublicShowcaseProvider';
import { ReCaptchaProvider } from '~/providers/ReCaptchaProvider';
import { SearchHeaderHeightProvider } from '~/providers/SearchHeaderHeightProvider';
import { getPublicShowcaseImmutableFilters } from '~/utils/showcase/getPublicShowcaseImmutableFilters';

// Live `/a` and Preview. Edit mounts `ShowcaseEditorLayout` instead. Viewer personality
// (`ownsDocument`, `rendersEmptyBlocks`) is hardcoded here so those forks cannot leak onto a
// private authoring surface again.
export type PublicShowcaseLayoutProps = Omit<
  PublicShowcaseContextValue,
  'ownsDocument' | 'rendersEmptyBlocks' | 'collection'
> & {
  // The subset of the collection record this tree consumes, composed from the direct children's
  // prop types (child-relative Picks): the page's reads, the PublicShowcaseProvider value the
  // leaves read, this layout's own `collection.id` (ShortIdProvider's objectId), and the workspace
  // fields the account/workspace providers receive. Narrowed to the consumed subset — rather than
  // the full public `CollectionWithInfo` — because Preview supplies the record from the PRIVATE
  // collection fetch, which honestly carries only these fields (see useShowcaseEditorCollection);
  // the live /a page's full public record still satisfies it.
  collection: Pick<GetByShortIDCollectionResponse['data'], 'id'> &
    PublicShowcasePageProps['collection'] &
    PublicShowcaseContextValue['collection'] & {
      workspace: Pick<GetByShortIDCollectionResponse['data']['workspace'], 'id' | 'name' | 'image'>;
    };
  permissions: ScopedPermissions;
  presentationSettings?: ShortUrlResponse['presentationSettings'];
  presentationContent?: ShortUrlResponse['presentationContent'];
  presentationPages?: ShortUrlResponse['presentationPages'];
  // Whether the link stores "Require name/email to view" — see PublicShowcaseRequireAccountGate.
  // REQUIRED, not `?`: an access gate that goes silently missing on a future surface is the failure
  // mode worth a compile error. Live passes the ShortUrl flag; Preview passes `false` because it
  // applies NO access gates — see the note on ShowcasePreview.
  requireAccount: boolean;
  // From `getByShortId.isOnPaidPlan` on the live viewer; Preview passes the owner's subscription.
  // Required so a new surface cannot silently omit the phishing gate. `undefined` is a real value
  // (rolling-deploy / missing field) and fails closed.
  isOnPaidPlan: boolean | undefined;
  shortId: string;
};

export const PublicShowcaseLayout = memo(
  ({
    activeBoardId,
    activePage,
    collection,
    getBoardNavigationHref,
    getPageNavigationHref,
    onNavigateToBoard,
    onSelectPage,
    permissions,
    presentationSettings,
    presentationContent,
    presentationPages,
    renderAsset,
    renderBlock,
    renderBoard,
    renderCover,
    renderFile,
    requireAccount,
    isOnPaidPlan,
    useGalleryViewItems,
    useNavPageBoards,
    useBoardGalleryViewItems,
    useBoardTableViewItems,
    SelectionBar,
    shortId,
  }: PublicShowcaseLayoutProps) => {
    return (
      // NOTE: no redux provider here. PublicShowcaseReduxProvider is mounted BY EACH SURFACE
      // above its PublicAssetModalNavigation provider (the /a route and the preview content),
      // because the navigation bindings dispatch into the collection tree's store — see
      // useSeedCentralizedPublicBoard. A new mount of this layout must mount redux + navigation first.
      <PublicShowcaseProvider
        collection={collection}
        activeBoardId={activeBoardId}
        activePage={activePage}
        getBoardNavigationHref={getBoardNavigationHref}
        getPageNavigationHref={getPageNavigationHref}
        onNavigateToBoard={onNavigateToBoard}
        onSelectPage={onSelectPage}
        ownsDocument
        useGalleryViewItems={useGalleryViewItems}
        useNavPageBoards={useNavPageBoards}
        useBoardGalleryViewItems={useBoardGalleryViewItems}
        useBoardTableViewItems={useBoardTableViewItems}
        renderAsset={renderAsset}
        renderBlock={renderBlock}
        renderBoard={renderBoard}
        renderCover={renderCover}
        renderFile={renderFile}
        rendersEmptyBlocks={false}
        SelectionBar={SelectionBar}
      >
        <ExternalLinksClickableProvider areExternalLinksClickable={isOnPaidPlan === true}>
          <ShortIdProvider
            shortId={shortId}
            objectId={collection.id}
            objectType="collection"
            isOnPaidPlan={isOnPaidPlan}
            presentationSettings={presentationSettings}
            presentationContent={presentationContent}
            presentationPages={presentationPages}
          >
            <ReCaptchaProvider siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
              <AccountProvisionerProvider>
                <PublicAccountProvider workspace={collection.workspace}>
                  <PublicPermissionsProvider permissions={permissions}>
                    <PublicWorkspaceProvider
                      workspaceId={collection.workspace.id}
                      workspaceImage={collection.workspace.image}
                      workspaceName={collection.workspace.name}
                    >
                      <PublicShareWorkspaceAccessProvider>
                        <ErrorBoundary>
                          <ConnectedRouter>
                            <SearchHeaderHeightProvider>
                              <FiltersProvider getImmutableFilters={getPublicShowcaseImmutableFilters}>
                                <ModifiersKeysPressedProvider>
                                  <ModalProvider>
                                    <ThumbnailPreferenceContextProvider>
                                      <MaintenanceMode>
                                        <PublicBoardCustomFieldsProvider>
                                          <PublicBoardSocketContextProvider>
                                            <ActionBarProvider>
                                              <TableHeaderPositionContextProvider>
                                                <KanbanOffsetContextProvider>
                                                  {/* Wrapping the page declares which subtree the
                                                      access check is about; it does NOT condition
                                                      rendering. The gate always renders its
                                                      children and opens the sign-up modal over
                                                      them. It sits here because this is the
                                                      innermost point with ModalProvider,
                                                      ReCaptchaProvider and PublicAccountProvider
                                                      above it — the modal it opens needs them. */}
                                                  <PublicShowcaseRequireAccountGate requireAccount={requireAccount}>
                                                    <PublicShowcasePage
                                                      collection={collection}
                                                      presentationSettings={presentationSettings}
                                                    />
                                                  </PublicShowcaseRequireAccountGate>
                                                </KanbanOffsetContextProvider>
                                              </TableHeaderPositionContextProvider>
                                            </ActionBarProvider>
                                          </PublicBoardSocketContextProvider>
                                        </PublicBoardCustomFieldsProvider>
                                      </MaintenanceMode>
                                    </ThumbnailPreferenceContextProvider>
                                  </ModalProvider>
                                </ModifiersKeysPressedProvider>
                              </FiltersProvider>
                            </SearchHeaderHeightProvider>
                          </ConnectedRouter>
                        </ErrorBoundary>
                      </PublicShareWorkspaceAccessProvider>
                    </PublicWorkspaceProvider>
                  </PublicPermissionsProvider>
                </PublicAccountProvider>
              </AccountProvisionerProvider>
            </ReCaptchaProvider>
          </ShortIdProvider>
        </ExternalLinksClickableProvider>
      </PublicShowcaseProvider>
    );
  },
);

PublicShowcaseLayout.displayName = 'PublicShowcaseLayout';
